import { invariant } from '@ifixit/helpers';
import { LocaleCode } from '@ifixit/i18n/types';
import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { CartDrawer, CartDrawerProvider } from '@ifixit/cart';
import type { ElementType } from 'react';
import { SmartLink } from 'Shared/smart_link';
import { SupportLink } from 'Shared/support-link';

type CartDrawerWrapperProps = {
   locale: LocaleCode | null;
   storeCode: string;
   linkComponent?: ElementType;
};

export function CartDrawerWrapper({ locale, storeCode, linkComponent }: CartDrawerWrapperProps) {
   const { cartUrl } = useShopifyStorefrontClient();
   invariant(cartUrl, 'cart url must be provided for carpenter cart drawer');
   invariant(locale, 'locale must be provided for carpenter cart drawer');

   return (
      <CartDrawer
         localeCode={locale}
         ImageComponent={CartImage}
         supportLinkComponent={SupportLink}
         viewCartUrl={cartUrl}
         storeCode={storeCode}
         linkComponent={linkComponent}
         recordMerchExperiment={() => true}
      />
   );
}

function CartImage({ alt, src }: { alt?: string; src: string }) {
   const srcUrl = new URL(src);
   const params = new URLSearchParams(srcUrl.search);
   params.set('width', '70');
   const newSrc = `${srcUrl.origin}${srcUrl.pathname}?${params.toString()}`;
   return <img src={newSrc} alt={alt ?? ''} className="object-cover" />;
}

type MobileAppCartDrawerProps = {
   shopifyStore: {
      country: CountryCode;
      currencyCode: string;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
      cartUrl: string;
      storeUrl: string;
   };
   cartDrawer: {
      locale: LocaleCode | null;
   };
};

/**
 * The cart drawer without the rest of the header, mounted by
 * MobileAppCartDrawerComponent on native-app requests. The apps hide the
 * header chrome, but the guide "Add to cart" buttons open the drawer by
 * clicking its trigger (Shared/cart_drawer.js), so the trigger has to be in
 * the DOM. It is hidden: a dispatched click still reaches a display:none
 * element, and the drawer itself renders in a portal.
 */
export function MobileAppCartDrawer({ shopifyStore, cartDrawer }: MobileAppCartDrawerProps) {
   return (
      <ShopifyStorefrontProvider {...shopifyStore}>
         <CartDrawerProvider>
            <div hidden>
               <CartDrawerWrapper
                  locale={cartDrawer.locale}
                  storeCode={shopifyStore.storeCode}
                  linkComponent={SmartLink}
               />
            </div>
         </CartDrawerProvider>
      </ShopifyStorefrontProvider>
   );
}
